import React from 'react';
import { useSelector } from 'react-redux';
import PropTypes from 'prop-types';
import { trackEvent } from 'ddc-track-event';
import { setClassNames } from '@ddc/classnames-js';
import { usePrefs, useRequestData, useLabels } from 'wsm-common-data';
import { groupAliasesSelector } from '../server/selectors';
import { sanitizeEventKey } from '../utils/sanitizeEventKey';

const TabTitle = ({ title, id, isActive, onSelect, eventKey, tabIndex }) => {
	const labels = useLabels();
	const { widgetName, windowId } = useRequestData();
	const { showTabIcons, tabIconClasses } = usePrefs();
	const groupAliases = useSelector(groupAliasesSelector);

	const shouldShowIcon = showTabIcons === 'true';
	const tabId = `${id}-tab-${sanitizeEventKey(eventKey)}`;
	const tabLabel = labels.get(title);
	const tabTitleClasses = setClassNames([
		'mx-2',
		'tab-item',
		'text-nowrap',
		'ddc-font-size-base',
		'd-flex',
		'align-items-center',
		isActive && 'active'
	]);

	const onSelectTab = React.useCallback(() => {
		onSelect(eventKey);
		trackEvent(widgetName, windowId, {
			action: 'clicked',
			element: tabId,
			elementCTA: tabLabel,
			result: 'Model selector filtered'
		});
	}, [onSelect, eventKey, tabLabel, tabId, widgetName, windowId]);

	let iconElement = null;
	if (shouldShowIcon) {
		const alias = groupAliases[title] ?? title;
		const tabName = alias.toLowerCase().replace(/\s+/g, '-');
		iconElement = (
			<i
				data-testid={`tab-icon-${tabName}`}
				className={setClassNames([
					tabIconClasses.replace(/TAB_NAME/g, tabName)
				])}
				aria-hidden="true"
			/>
		);
	}

	return (
		<button
			href="#"
			role="tab"
			id={tabId}
			key={tabId}
			type="button"
			tabIndex={tabIndex}
			className={tabTitleClasses}
			aria-selected={isActive}
			onClick={onSelectTab}
			ui-tabs-initial-load="false"
			aria-controls={`${id}-pane-${sanitizeEventKey(eventKey)}`}
		>
			{iconElement}
			{title}
		</button>
	);
};

TabTitle.propTypes = {
	id: PropTypes.string,
	title: PropTypes.string,
	isActive: PropTypes.bool,
	onSelect: PropTypes.func,
	tabIndex: PropTypes.number,
	eventKey: PropTypes.string
};

export default TabTitle;
